import React, { ReactNode, useState } from 'react';
import {
  Box,
  Drawer,
  List,
  Typography,
  IconButton,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Avatar,
  Menu,
  MenuItem,
  Container,
  Divider,
  Tooltip,
} from '@mui/material';
import {
  Menu as MenuIcon,
  Dashboard as DashboardIcon,
  CalendarToday as CalendarIcon,
  Restaurant as RestaurantIcon,
  People as PeopleIcon,
  Settings as SettingsIcon,
  AccountCircle as AccountCircleIcon,
  Logout as LogoutIcon,
  AdminPanelSettings as AdminIcon,
  Tv as TvIcon,
  Business as BusinessIcon,
  Assessment as AssessmentIcon,
  CreditCard as CardIcon,
} from '@mui/icons-material';
import packageJson from '../../package.json';
import { useAuth } from '../contexts/AuthContext';
import { useNavigate, Link, useLocation } from 'react-router-dom';
import foodFlowLogo from '../assets/foodflow-logo-w.png';
import foodFlowSecondaryLogo from '../assets/foodflow-w.png';
import { systemSettingsAPI, getImageUrl } from '../services/api';

const DRAWER_WIDTH = 260;

const isSuperAdminDomain = () => {
  const hostname = window.location.hostname;
  return hostname.startsWith('co.') || hostname.includes('superadmin');
};

interface LayoutProps {
  children: ReactNode;
}

const Layout: React.FC<LayoutProps> = ({ children }) => {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [mobileOpen, setMobileOpen] = useState(false);
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [customLogo, setCustomLogo] = useState<string | null>(null);
  const [restaurantName, setRestaurantName] = useState<string>(user?.companyTitle || 'فودفلو');

  React.useEffect(() => {
    const fetchLogo = async () => {
      try {
        const settings = await systemSettingsAPI.getPublicSettings(user?.organizationId);
        if (settings.logoUrl) {
          setCustomLogo(getImageUrl(settings.logoUrl));
        }
        if (settings.restaurantName) {
          setRestaurantName(settings.restaurantName);
        }
      } catch (err) {
        console.error('Failed to fetch public settings', err);
      }
    };
    fetchLogo();

    const handleSettingsUpdate = (event: Event) => {
      const customEvent = event as CustomEvent;
      if (customEvent.detail) {
        setCustomLogo(customEvent.detail.logoUrl ? getImageUrl(customEvent.detail.logoUrl) : null);
        if (customEvent.detail.restaurantName) {
          setRestaurantName(customEvent.detail.restaurantName);
        }
      }
    };

    window.addEventListener('settingsUpdated', handleSettingsUpdate);
    return () => {
      window.removeEventListener('settingsUpdated', handleSettingsUpdate);
    };
  }, []);

  React.useEffect(() => {
    if (isSuperAdminDomain()) {
      document.title = 'سوپر ادمین فودفلو';
    } else {
      document.title = `${restaurantName} - مدیریت غذای فودفلو`;
    }
  }, [restaurantName]);

  const handleDrawerToggle = () => {
    setMobileOpen(!mobileOpen);
  };

  const handleMenuOpen = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
  };

  const handleLogout = () => {
    logout();
    navigate('/login');
    handleMenuClose();
  };

  // Check if user has specific permission
  const hasPermission = (permission: string) => {
    if (!user) return false;
    if (user.role === 'admin' || user.role === 'superadmin') return true;
    if (user.role === 'manager') {
      return user.permissions?.includes(permission) || false;
    }
    return false;
  };

  interface MenuItemType {
    path: string;
    label: string;
    icon: React.ReactNode;
    isHighlighted?: boolean;
  }

  const getMenuItems = (): MenuItemType[] => {
    const items: MenuItemType[] = [
      { path: '/dashboard', label: 'داشبورد', icon: <DashboardIcon /> }, // Always visible
    ];

    const canMonitor = user?.role === 'admin' || user?.role === 'superadmin' || hasPermission('monitor');
    if (canMonitor) {
      items.push({ path: '/monitor', label: 'مانیتور رستوران', icon: <TvIcon />, isHighlighted: true });
    }

    if (hasPermission('menu')) {
      items.push({ path: '/daily-planning', label: 'برنامه غذایی', icon: <CalendarIcon /> });
    }

    if (isSuperAdminDomain() && user?.role === 'superadmin') {
      return [
        { path: '/dashboard', label: 'پیشخوان سیستم', icon: <DashboardIcon /> },
        { path: '/admin/organizations', label: 'مدیریت سازمان‌ها', icon: <BusinessIcon /> },
        { path: '/admin/settings', label: 'تنظیمات کلان', icon: <SettingsIcon /> },
      ];
    }

    if (user?.role === 'admin' || user?.role === 'superadmin') {
      items.push(
        { path: '/manager/daily-reservations', label: 'رزروها', icon: <PeopleIcon /> },
        { path: '/admin/guest-reservations', label: 'رزرو مهمان', icon: <PeopleIcon /> },
        { path: '/admin/reports', label: 'گزارشات', icon: <AssessmentIcon /> },
        { path: '/admin/delivery-groups', label: 'تحویل گروهی', icon: <CardIcon /> },
        { path: '/admin/meals', label: 'آیتم‌های غذایی', icon: <RestaurantIcon /> },
        { path: '/admin/users', label: 'پرسنل', icon: <PeopleIcon /> },
        { path: '/admin/settings', label: 'تنظیمات', icon: <SettingsIcon /> },
      );
    } else {
      // Manager specific Items (Secondary Admins)
      if (hasPermission('reservations')) {
        items.push({ path: '/manager/daily-reservations', label: 'رزروها', icon: <PeopleIcon /> });
      }
      if (hasPermission('reports')) {
        items.push({ path: '/admin/reports', label: 'گزارشات', icon: <AssessmentIcon /> });
      }
      if (hasPermission('meals')) {
        // Point to the full-featured AdminMeals component
        items.push({ path: '/admin/meals', label: 'آیتم‌های غذایی', icon: <RestaurantIcon /> });
      }
      if (hasPermission('users')) {
        // Point to the full-featured AdminUsers component
        items.push({ path: '/admin/users', label: 'پرسنل', icon: <PeopleIcon /> });
      }
      if (hasPermission('guest-reservations')) {
        items.push({ path: '/admin/guest-reservations', label: 'رزرو مهمان', icon: <PeopleIcon /> });
      }
      if (hasPermission('delivery-groups')) {
        items.push({ path: '/admin/delivery-groups', label: 'تحویل گروهی', icon: <CardIcon /> });
      }
      if (hasPermission('settings')) {
        items.push({ path: '/admin/settings', label: 'تنظیمات', icon: <SettingsIcon /> });
      }
    }

    return items;
  };

  const menuItems = getMenuItems();
  const isSuperAdmin = isSuperAdminDomain();
  const bottomPaths = ['/admin/settings', '/admin/users', '/admin/meals'];
  const mainMenuItems = menuItems.filter(item => !bottomPaths.includes(item.path));
  const bottomMenuItems = menuItems.filter(item => bottomPaths.includes(item.path));

  const getIconColor = (path: string) => {
    if (isSuperAdmin) {
      if (path === '/admin/settings') return 'linear-gradient(135deg, #8B5CF6, #6D28D9)';
      if (path === '/admin/users') return 'linear-gradient(135deg, #3B82F6, #2563EB)';
      if (path === '/admin/meals') return 'linear-gradient(135deg, #EF4444, #DC2626)';
      return 'linear-gradient(135deg, #22C55E, #16A34A)';
    }
    if (path === '/admin/settings') return 'linear-gradient(45deg, #64748b, #94a3b8)';
    if (path === '/admin/users') return 'linear-gradient(45deg, #8b5cf6, #c4b5fd)';
    if (path === '/admin/meals') return 'linear-gradient(45deg, #ef4444, #fca5a5)';
    return 'linear-gradient(45deg, #3b82f6, #93c5fd)';
  };

  const getMainItemColor = (isActive: boolean) => {
    if (isSuperAdmin) {
      if (isActive) return 'linear-gradient(135deg, #22C55E, #16A34A)';
      return 'linear-gradient(135deg, rgba(30, 41, 59, 0.8), rgba(15, 23, 42, 0.9))';
    }
    if (isActive) return 'linear-gradient(45deg, #eab308, #fef08a)'; // Yellow
    return 'linear-gradient(45deg, #3b82f6, #93c5fd)'; // Blue
  };

  const drawer = (
    <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
      {/* Logo at top */}
      <Box
        sx={{
          pt: 4,
          pb: 2,
          px: 2,
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          gap: 2,
        }}
      >
        <Box
          component="img"
          src={isSuperAdminDomain() ? foodFlowSecondaryLogo : (customLogo || foodFlowSecondaryLogo)}
          alt="Logo"
          sx={{
            width: 180,
            height: 120,
            objectFit: 'contain',
            mb: 0
          }}
        />

        {/* User Profile Compact */}
        <Box sx={{ width: '100%' }}>
          <ListItemButton
            onClick={handleMenuOpen}
            sx={{
              borderRadius: 2,
              '&:hover': { backgroundColor: 'rgba(91, 124, 250, 0.1)' },
              width: '100%',
            }}
          >
            <ListItemIcon sx={{ minWidth: 40 }}>
              <Avatar sx={{ width: 32, height: 32, bgcolor: 'primary.main' }}>
                <AccountCircleIcon fontSize="small" />
              </Avatar>
            </ListItemIcon>
            <ListItemText
              primary={user?.firstName || user?.lastName ? `${user.firstName || ''} ${user.lastName || ''}`.trim() : user?.mobile || ''}
              secondary={user?.firstName || user?.lastName ? user?.mobile : (user?.role === 'superadmin' ? 'سوپر ادمین' : user?.role === 'admin' ? 'مدیر کل' : 'مدیر سیستم')}
              primaryTypographyProps={{ fontFamily: 'Vazir, sans-serif', color: 'white', fontWeight: 600, fontSize: '0.9rem' }}
              secondaryTypographyProps={{ fontFamily: 'Vazir, sans-serif', color: 'rgba(255,255,255,0.7)', fontSize: '0.75rem' }}
            />
          </ListItemButton>
          <Menu
            id="user-menu"
            anchorEl={anchorEl}
            open={Boolean(anchorEl)}
            onClose={handleMenuClose}
            anchorOrigin={{
              vertical: 'bottom',
              horizontal: 'center',
            }}
            transformOrigin={{
              vertical: 'top',
              horizontal: 'center',
            }}
            PaperProps={{
              sx: {
                mt: 1,
                minWidth: 240,
                background: '#252b4a',
                border: '1px solid rgba(91, 124, 250, 0.3)',
              },
            }}
          >
            <MenuItem onClick={handleLogout} sx={{ fontFamily: 'Vazir, sans-serif' }}>
              <ListItemIcon>
                <LogoutIcon fontSize="small" />
              </ListItemIcon>
              <ListItemText primary="خروج" />
            </MenuItem>
          </Menu>
        </Box>
      </Box>

      {/* Navigation Links */}
      <List sx={{ px: 2, flex: 1, overflowY: 'auto' }}>
        {(() => {
          const renderedPaths = new Set();
          
          const renderItem = (item: any, isFlexChild = false) => {
            const isActive = location.pathname === item.path;
            const buttonContent = (
                <ListItemButton
                  component={Link}
                  to={item.path}
                  selected={isActive}
                  sx={{
                    minHeight: 52,
                    mb: isFlexChild ? 0 : 1.5,
                    borderRadius: '16px',
                    background: getMainItemColor(isActive),
                    color: isActive ? '#1e40af' : '#fff',
                    boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
                    opacity: isActive ? 1 : 0.85,
                    transition: 'all 0.2s',
                    display: 'flex',
                    flexDirection: 'row',
                    justifyContent: isFlexChild ? 'center' : 'flex-start',
                    alignItems: 'center',
                    px: isFlexChild ? 1 : 2,
                    width: '100%',
                    flex: isFlexChild ? 1 : 'none',
                    '&:hover': {
                      transform: 'translateY(-2px)',
                      boxShadow: '0 6px 16px rgba(0,0,0,0.25)',
                      opacity: 1,
                    },
                    '&.Mui-selected': {
                      backgroundColor: 'transparent',
                      boxShadow: '0 4px 16px rgba(0,0,0,0.3)',
                      border: '2px solid rgba(255,255,255,0.4)',
                      '&:hover': {
                        backgroundColor: 'transparent',
                      }
                    }
                  }}
                >
                  <ListItemIcon
                    sx={{
                      minWidth: isFlexChild ? 32 : 40,
                      color: 'inherit',
                      display: 'flex',
                      justifyContent: 'center'
                    }}
                  >
                    {item.icon}
                  </ListItemIcon>
                  <ListItemText
                    primary={item.label}
                    primaryTypographyProps={{
                      fontFamily: 'Vazir, sans-serif',
                      fontWeight: isActive ? 600 : 500,
                      fontSize: isFlexChild ? '0.8rem' : '0.9rem',
                      whiteSpace: 'nowrap'
                    }}
                    sx={{ m: 0, flex: isFlexChild ? 'none' : '1 1 auto', textAlign: isFlexChild ? 'center' : 'left' }}
                  />
                </ListItemButton>
            );

            if (isFlexChild) return buttonContent;

            return (
              <ListItem key={item.path} disablePadding>
                {buttonContent}
              </ListItem>
            );
          };

          return mainMenuItems.map((item) => {
            if (renderedPaths.has(item.path)) return null;

            if (item.path === '/manager/daily-reservations' || item.path === '/admin/guest-reservations') {
              const dailyResItem = mainMenuItems.find(x => x.path === '/manager/daily-reservations');
              const guestResItem = mainMenuItems.find(x => x.path === '/admin/guest-reservations');
              
              if (dailyResItem && guestResItem) {
                renderedPaths.add(dailyResItem.path);
                renderedPaths.add(guestResItem.path);
                return (
                  <ListItem key="res-pair" disablePadding sx={{ mb: 1.5 }}>
                    <Box sx={{ display: 'flex', width: '100%', gap: 1.5 }}>
                      {renderItem(dailyResItem, true)}
                      {renderItem(guestResItem, true)}
                    </Box>
                  </ListItem>
                );
              }
            }

            renderedPaths.add(item.path);
            return renderItem(item, false);
          });
        })()}

        {/* Action Icons (Settings, Users, Meals) - Flowing sequentially */}
        {bottomMenuItems.length > 0 && (
          <ListItem disablePadding sx={{ mb: 1.5 }}>
            <Box sx={{ display: 'flex', width: '100%', gap: 1.5 }}>
              {bottomMenuItems.map((item) => (
                <Box key={item.path} sx={{ display: 'flex', flex: 1 }}>
                  <Tooltip title={item.label} arrow placement="top">
                    <IconButton
                      component={Link}
                      to={item.path}
                      sx={{
                        background: getIconColor(item.path),
                        color: '#fff',
                        width: '100%',
                        height: 52,
                        borderRadius: '16px',
                        boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
                        transition: 'all 0.2s',
                        '&:hover': {
                          transform: 'translateY(-2px)',
                          boxShadow: '0 6px 16px rgba(0,0,0,0.25)',
                        }
                      }}
                    >
                      {React.cloneElement(item.icon as React.ReactElement<any>, { fontSize: 'medium' })}
                    </IconButton>
                  </Tooltip>
                </Box>
              ))}
            </Box>
          </ListItem>
        )}
      </List>



      {/* Version at bottom */}
      <Box
        sx={{
          p: 1,
          borderTop: '1px solid rgba(91, 124, 250, 0.2)',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          gap: 0.5,
        }}
      >
        <Box
          component="img"
          src={foodFlowSecondaryLogo}
          alt="FoodFlow"
          sx={{
            height: 30,
            opacity: 0.8,
            mb: 0
          }}
        />
        <Typography
          variant="caption"
          align="center"
          sx={{
            fontFamily: 'Vazir, sans-serif',
            color: 'text.secondary',
            fontSize: '0.7rem',
            fontWeight: 500,
            opacity: 0.8,
          }}
        >
          Badban Creative Studio
        </Typography>
        <Typography
          variant="caption"
          align="center"
          sx={{
            fontFamily: 'Vazir, sans-serif',
            color: 'text.secondary',
            fontSize: '0.65rem',
            opacity: 0.6,
          }}
        >
          نسخه {packageJson.version}
        </Typography>
      </Box>
    </Box>
  );

  return (
    <Box sx={{ display: 'flex', height: '100vh', overflow: 'hidden', background: isSuperAdmin ? '#020617' : '#143970' }}>
      <Box
        component="nav"
        sx={{ width: { md: DRAWER_WIDTH + 32 }, flexShrink: { md: 0 } }}
      >
        <Drawer
          variant="temporary"
          anchor="left"
          open={mobileOpen}
          onClose={handleDrawerToggle}
          ModalProps={{
            keepMounted: true,
          }}
          sx={{
            display: { xs: 'block', md: 'none' },
            '& .MuiDrawer-paper': {
              boxSizing: 'border-box',
              width: DRAWER_WIDTH,
              background: isSuperAdmin
                ? 'linear-gradient(180deg, #0F172A 0%, #020617 100%)'
                : 'linear-gradient(180deg, #306fb6 0%, #2b63a2 100%)',
              border: isSuperAdmin
                ? '1px solid rgba(248, 250, 252, 0.06)'
                : '1px solid rgba(255, 255, 255, 0.1)',
              borderRadius: '24px',
              margin: '16px',
              height: 'calc(100% - 32px)',
              boxShadow: isSuperAdmin
                ? '0 8px 32px rgba(0,0,0,0.5)'
                : '0 8px 32px rgba(0,0,0,0.2)',
              display: 'flex',
              flexDirection: 'column',
              overflow: 'hidden'
            },
          }}
        >
          {drawer}
        </Drawer>
        {/* Mobile menu button */}
        <IconButton
          color="inherit"
          aria-label="open drawer"
          edge="end"
          onClick={handleDrawerToggle}
          sx={{
            position: 'fixed',
            top: 24,
            right: 24,
            zIndex: (theme) => theme.zIndex.drawer + 1,
            display: { md: 'none' },
            backgroundColor: 'rgba(91, 124, 250, 0.2)',
            backdropFilter: 'blur(10px)',
            '&:hover': {
              backgroundColor: 'rgba(91, 124, 250, 0.4)',
            },
          }}
        >
          <MenuIcon />
        </IconButton>
        <Drawer
          variant="permanent"
          anchor="left"
          sx={{
            display: { xs: 'none', md: 'block' },
            '& .MuiDrawer-paper': {
              boxSizing: 'border-box',
              width: DRAWER_WIDTH,
              background: isSuperAdmin
                ? 'linear-gradient(180deg, #0F172A 0%, #020617 100%)'
                : 'linear-gradient(180deg, #306fb6 0%, #2b63a2 100%)',
              border: isSuperAdmin
                ? '1px solid rgba(248, 250, 252, 0.06)'
                : '1px solid rgba(255, 255, 255, 0.1)',
              borderRadius: '24px',
              margin: '16px',
              height: 'calc(100% - 32px)',
              boxShadow: isSuperAdmin
                ? '0 8px 32px rgba(0,0,0,0.5)'
                : '0 8px 32px rgba(0,0,0,0.2)',
              display: 'flex',
              flexDirection: 'column',
              overflow: 'hidden'
            },
          }}
          open
        >
          {drawer}
        </Drawer>
      </Box>
      <Box
        component="main"
        sx={{
          flexGrow: 1,
          px: location.pathname === '/monitor' ? 3 : '50px',
          py: 3,
          width: { md: `calc(100% - ${DRAWER_WIDTH + 32}px)` },
          height: '100%',
          overflowY: 'auto',
          mt: 0,
          background: isSuperAdmin ? '#020617' : '#143970',
          backgroundAttachment: 'fixed',
        }}
      >
        <Container maxWidth={false} disableGutters>
          {children}
        </Container>
      </Box>
    </Box>
  );
};

export default Layout;
